import { getBoundingBox, getRotationAngle, getScaleDifference } from './math'
import { Coordinates, TransformerActionState, TransformerState } from './state'

type TransformerAction = {
	type: 'handleOffsetChange'
	actionState: TransformerActionState
	dragCoordinates: Coordinates
	snap: boolean
} | {
	type: 'handleRotationChange'
	actionState: TransformerActionState
	dragCoordinates: Coordinates
	snap: boolean
} | {
	type: 'handleScaleChange'
	actionState: TransformerActionState
	dragCoordinates: Coordinates
	forceCenterScale?: boolean
}

export function transformerReducer(state: TransformerState, action: TransformerAction): TransformerState {
	switch (action.type) {
		case 'handleOffsetChange':
			if (action.actionState?.type !== 'offset') {
				return state
			}

			// find new offsets
			let newOffsetX = state.offset.x + action.dragCoordinates.x - action.actionState.start.x
			let newOffsetY = state.offset.y + action.dragCoordinates.y - action.actionState.start.y

			// if snapping, round to nearest 4px
			if (action.snap) {
				newOffsetX = Math.round(newOffsetX / 4) * 4
				newOffsetY = Math.round(newOffsetY / 4) * 4
			}

			return {
				...state,
				offset: {
					x: newOffsetX,
					y: newOffsetY,
				},
			}
		case 'handleRotationChange':
			if (action.actionState?.type !== 'rotation') {
				return state
			}

			// get angle delta
			let rotation = getRotationAngle(action.actionState.center, action.actionState.start, action.dragCoordinates)

			// if snapping, round to the closest 16th of a turn
			if (action.snap) {
				rotation = Math.round(rotation / (Math.PI / 16)) * (Math.PI / 16)
			}

			return {
				...state,
				rotation: state.rotation + rotation,
			}
		case 'handleScaleChange':
			if (action.actionState?.type !== 'scale') {
				return state
			}

			// get different in scale
			let scaleMultiplier = getScaleDifference(
				action.actionState.center,
				action.actionState.start,
				action.dragCoordinates,
			)

			// if not from center, we need to dampen the effect by 50%
			// (since we're only scaling in one direction)
			if (!action.forceCenterScale) {
				scaleMultiplier = (scaleMultiplier - 1) * 0.5 + 1
			}

			const startBoundingBox = action.actionState.startBoundingBox

			// get new bounding box
			const endBoundingBox = getBoundingBox({
				x: state.offset.x,
				y: state.offset.y,
				w: action.actionState.size.width,
				h: action.actionState.size.height,
				scale: state.scale * scaleMultiplier,
				rotate: state.rotation,
			})

			let offsetX = 0
			let offsetY = 0

			// if not from center, compare the position of the anchor point in
			// the start and end bounding boxes, and offset by that
			if (!action.forceCenterScale) {
				offsetX = startBoundingBox[action.actionState.anchor].x - endBoundingBox[action.actionState.anchor].x
				offsetY = startBoundingBox[action.actionState.anchor].y - endBoundingBox[action.actionState.anchor].y
			}

			return {
				...state,
				scale: action.actionState.startScale * scaleMultiplier,
				offset: {
					x: state.offset.x + offsetX,
					y: state.offset.y + offsetY,
				},
			}
	}
}
